import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Tooltip,
  Chip,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  ClickAwayListener,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Typography, PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import {
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Visibility as ViewIcon,
  AccountBalance as BankIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FileUpload as ExportIcon2,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { TransactionSummaryCards, ExportDrawer } from '../../components';
import { FilterPanel } from '../../components/TransactionFilters/FilterPanel';
import { TRANSACTION_STATUSES, getStatusColor, type TransactionStatus } from '../../constants';
import type { Transaction, TransactionFilters } from '../../types';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { formatCurrency } from '@/utils/formatters';
import { format as formatMomentDate } from '@/utils/time';
import { transactionService } from '@/services/transactionService';



export const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isExportDrawerOpen, setIsExportDrawerOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [merchantTransactionSearchTerm, setMerchantTransactionSearchTerm] = useState('');
  const [transactionStats, setTransactionStats] = useState('');
  const [statusFiltered, setStatusFilter] = useState("transactionId"); // default

  // Filter panel state
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filters, setFilters] = useState<TransactionFilters>({
    searchTerm: '',
    statusFilter: '',
    dateRange: { startDate: '', endDate: '' },
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50); // Default rows per page

  // Sorting state
  const [sortField, setSortField] = useState<keyof Transaction>('transaction_date_time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Load transactions with server-side pagination
  useEffect(() => {
    loadTransactions();
    loadTransactionStats();
  }, [page, rowsPerPage, sortField, sortDirection]);

  const loadTransactions = async () => {
    setIsLoading(true);
    try {
      const currentPage = page + 1; // Convert 0-based to 1-based page
      const apiResponse = await transactionService.getTransactions(currentPage, rowsPerPage);

      setTransactions(apiResponse.results || []);
      setTotalCount(apiResponse.count || 0);
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTransactionStats = async () => {
    try {
      const stats = await transactionService.getTransactionStats();
      setTransactionStats(stats);
    } catch (error) {
      console.error('Error loading transaction stats:', error);
      // Keep default stats on error
    }
  };



  // Client-side search filtering (only for search term)
  const filteredTransactions = transactions.filter(txn => {
    if (!searchTerm) return true;
    return (
      txn.transaction_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.merchant_transaction_id?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Breadcrumbs
  const breadcrumbConfig = getBreadcrumbConfig('/transactions');
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
    customBreadcrumbs: breadcrumbConfig.hideBreadcrumbs ? [] : undefined,
  });

  const finalBreadcrumbs = breadcrumbs.length > 0
    ? breadcrumbs
    : [
      {
        label: 'Dashboard',
        href: '/merchant/dashboard',
        onClick: () => navigate('/merchant/dashboard'),
      },
      {
        label: 'Transactions',
        active: true,
      },
    ];

  // Formatters
  const formatTransactionCurrency = (amount: number): string =>
    formatCurrency(amount, 'INR', 'en-IN');

  const formatTransactionDate = (dateString: string): string =>
    formatMomentDate(dateString, 'MMM DD, YYYY HH:mm');

  // Subtitle generator
  // const getSubtitle = () => {
  //   const parts: string[] = [];

  //   if (statusFilter) {
  //     parts.push(`Status: ${TRANSACTION_STATUSES[statusFilter as TransactionStatus] || statusFilter}`);
  //   }

  //   if (searchTerm) {
  //     parts.push(`Search: "${searchTerm}"`);
  //   }

  //   const total = searchTerm ? filteredTransactions.length : totalCount;
  //   const base = 'Manage payment transactions';

  //   return parts.length > 0
  //     ? `${base} • ${parts.join(' • ')} (${total} ${total === 1 ? 'transaction' : 'transactions'})`
  //     : `${base} (${total} ${total === 1 ? 'transaction' : 'transactions'})`;
  // };


  // Pagination handlers - these will trigger API calls via useEffect
  const handleChangePage = (_event: unknown, newPage: number) => {
    setIsLoading(true);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  // Sorting handlers - these will trigger API calls via useEffect
  // const handleSort = (field: keyof Transaction) => {
  //   setIsLoading(true);
  //   if (sortField === field) {
  //     setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  //   } else {
  //     setSortField(field);
  //     setSortDirection('asc');
  //   }
  //   setPage(0); // Reset to first page when sorting
  // };

  // Get sort icon for column
  const getSortIcon = (field: keyof Transaction) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };

  // Table columns configuration
  const columns = [
    { field: 'transaction_id', headerName: 'Payment ID', sortable: true, align: 'left' as const },
    { field: 'merchant_transaction_id', headerName: 'Merchant Payment ID', sortable: true, align: 'left' as const },
    { field: 'transaction_amount', headerName: 'Amount', sortable: true, align: 'right' as const },
    { field: 'payment_gateway', headerName: 'Payment Gateway', sortable: true, align: 'left' as const },
    { field: 'transaction_status', headerName: 'Status', sortable: true, align: 'center' as const },
    { field: 'transaction_date_time', headerName: 'Created at', sortable: true, align: 'left' as const },
  ];

  // Drawer handlers
  const handleOpenFilterDrawer = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsFilterDrawerOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setIsFilterDrawerOpen(false);
    setSelectedTransaction(null);
  };

  const onClearFilters = () => {
    setIsLoading(true);
    setSearchTerm('');
    setFilters({
      searchTerm: '',
      statusFilter: '',
      dateRange: { startDate: '', endDate: '' },
    });
    setPage(0);
    loadTransactions();
    setStatusFilter('transactionId');
    setTransactionSearchTerm('');
  };

  const handleFilterPanelOpen = () => {
    setIsFilterPanelOpen(true);
  };

  const handleFilterPanelClose = () => {
    setIsFilterPanelOpen(false);
  };

  const handleFiltersChange = (newFilters: TransactionFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = async () => {
    setIsLoading(true);
    try {
      setPage(0);

      // Apply date range filter if selected
      if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
        const formatDate = (dateStr: string) => {
          const date = new Date(dateStr);
          return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        };

        const startDate = formatDate(filters.dateRange.startDate);
        const endDate = formatDate(filters.dateRange.endDate);
        const response = await transactionService.getTransactionDateRangeData(startDate, endDate);
        setTransactions(response.results || []);
        setTotalCount(response.count || 0);
        setIsFilterPanelOpen(false);
        return;
      }

      // Apply status filter if selected
      if (filters.statusFilter) {
        const statuses = filters.statusFilter.split(',').filter(Boolean);
        if (statuses.length > 0) {
          const response = await transactionService.getTransactionStatusData(statuses);
          setTransactions(response.results || []);
          setTotalCount(response.count || 0);
          setIsFilterPanelOpen(false);
          return;
        }
      }

      // If no filters, reload all transactions
      await loadTransactions();
    } catch (error) {
      console.error('Error applying filters:', error);
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setIsFilterPanelOpen(false);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({
      searchTerm: '',
      statusFilter: '',
      dateRange: { startDate: '', endDate: '' },
    });
    onClearFilters();
  };

  // Search handler: fetch transactions by search terms
  const onSearchTermChange = async () => {
    const transactionId = transactionSearchTerm.trim();
    const merchantId = merchantTransactionSearchTerm.trim();

    if (transactionId || merchantId) {
      try {
        const response = await transactionService.getTransactionData(
          transactionId || undefined,
          merchantId || undefined
        );

        // Update state - this will trigger re-render
        setTransactions(response.results || []);
        setTotalCount(response.count || 0);
        setSearchTerm(transactionId || merchantId);
      } catch (error) {
        setTransactions([]);
        console.error('Fetch error:', error);
      }
    } else {
      setTransactions([]);
      setSearchTerm('');
    }
  };

  // Individual search term handlers
  const handleTransactionSearchChange = (value: string) => {
    setTransactionSearchTerm(value);
  };

  const handleMerchantTransactionSearchChange = (value: string) => {
    setMerchantTransactionSearchTerm(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchTermChange();
    }
  };

  // const onStatusFilterChangeData = (value: string) => {
  //   setTransactionSearchStatusTerm(value);
  //   onStatusFilterChangeData();
  // };


  // Header Actions
  const headerActions: PageHeaderAction[] = [
    {
      label: 'Filter',
      icon: <FilterIcon sx={{ fontSize: 16 }} />,
      onClick: () => setIsFilterDrawerOpen(true),
      variant: 'outlined',
      size: 'medium',
    },
    {
      label: 'Export',
      icon: <ExportIcon sx={{ fontSize: 16 }} />,
      onClick: () => setIsExportDrawerOpen(true),
      variant: 'outlined',
      size: 'medium',
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Payments"
        //subtitle={getSubtitle()}
        //breadcrumbs={finalBreadcrumbs}
        //actions={headerActions}
        showDivider={false}
      />

      <TransactionSummaryCards
        stats={transactionStats}
      />

      <Box
        mb={3}
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={{ xs: 2, md: 2 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 2, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flex={1}
        >
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: { xs: '100%', sm: 180 },
              '& .MuiOutlinedInput-root': {
                background: 'white',
              },
            }}
          >
            <Select
              value={statusFiltered}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="transactionId">Payment ID</MenuItem>
              <MenuItem value="merchantId">Merchant Payment ID</MenuItem>
            </Select>
          </FormControl>

          {statusFiltered === "transactionId" &&

            < TextField
              variant="outlined"
              type="search"
              placeholder="Search"
              value={transactionSearchTerm}
              onChange={(e) => handleTransactionSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: { xs: '60%', sm: 300, md: 300 },
                '& .MuiOutlinedInput-root': {
                  background: 'white',
                },
              }}
            />
          }

          {statusFiltered === "merchantId" &&
            <TextField
              variant="outlined"
              type="search"
              placeholder="Search"
              value={merchantTransactionSearchTerm}
              onChange={(e) => handleMerchantTransactionSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: { xs: '60%', sm: 300, md: 300 },
                '& .MuiOutlinedInput-root': {
                  background: 'white',
                },
              }}
            />
          }

          {/* Clear Filters Button */}
          <Button
            size="small"
            onClick={onClearFilters}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          >
            Clear Filters
          </Button>
        </Box>

        {/* Action Buttons */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 1, sm: 1 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          {/* <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none' }}
          >
            Batch refunds
          </Button> */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon sx={{ color: '#17A6DA' }} />}
            onClick={() => loadTransactions()}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ExportIcon sx={{ color: '#17A6DA' }} />}
            onClick={() => setIsExportDrawerOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Add Filters Button */}
      <Button
        ref={filterButtonRef}
        variant="outlined"
        size="small"
        onClick={handleFilterPanelOpen}
        startIcon={<AddIcon sx={{ color: '#17A6DA' }} />}
        endIcon={<FilterIcon sx={{ color: '#17A6DA' }} />}
        sx={{
          minWidth: { xs: '100%', sm: 'auto' },
          textTransform: 'none',
        }}
      >
        Add filters
      </Button>

      {/* Filter Panel */}
      {isFilterPanelOpen && (
        <ClickAwayListener onClickAway={handleFilterPanelClose}>
          <Box>
            <FilterPanel
              open={isFilterPanelOpen}
              anchorEl={filterButtonRef.current}
              onClose={handleFilterPanelClose}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearAllFilters}
            />
          </Box>
        </ClickAwayListener>
      )}


      {/* HTML Table with Pagination */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
        <TableContainer sx={{
          maxHeight: { xs: 400, sm: 600 },
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: 4,
          },
        }}>
          <Table stickyHeader sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.align}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      cursor: column.sortable ? 'pointer' : 'default',
                      '&:hover': column.sortable ? { backgroundColor: '#e0e0e0' } : {},
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                      whiteSpace: 'nowrap',
                    }}
                  //onClick={() => column.sortable && handleSort(column.field as keyof Transaction)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {column.headerName}
                      {/* {column.sortable && getSortIcon(column.field as keyof Transaction)} */}
                    </Box>
                  </TableCell>
                ))}
                <TableCell sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>

              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography variant="body1" color="textSecondary">
                        Loading transactions...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredTransactions.length === 0 ? (

                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No Transactions found matching your search criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTransactions.map((transaction) => (
                  <TableRow key={transaction.transaction_id} hover>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', '&:hover .copy-icon': { opacity: 1 } }}>
                        <Typography variant="body2" fontWeight="medium" fontFamily="monospace" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {String(transaction.transaction_id)}
                        </Typography>
                        <Tooltip title="Copy to clipboard">
                          <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(String(transaction.transaction_id))}
                            className="copy-icon"
                            sx={{ ml: 0.5, p: 0.25, opacity: 0, transition: 'opacity 0.2s' }}
                          >
                            <ContentCopyIcon sx={{ fontSize: '15px' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', '&:hover .copy-icon': { opacity: 1 } }}>
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', color: '#17A6DA' } }}>
                          {String(transaction.merchant_transaction_id)}
                        </Typography>
                        <Tooltip title="Copy to clipboard">
                          <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(String(transaction.merchant_transaction_id))}
                            className="copy-icon"
                            sx={{ ml: 0.5, p: 0.25, opacity: 0, transition: 'opacity 0.2s' }}
                          >
                            <ContentCopyIcon sx={{ fontSize: '15px' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="center" color="success" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {formatTransactionCurrency(Number(transaction.transaction_amount))}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {transaction.payment_gateway && typeof transaction.payment_gateway === 'object' && 'payment_gateway_name' in transaction.payment_gateway
                            ? String((transaction.payment_gateway as any).payment_gateway_name)
                            : 'N/A'
                          }
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title={String(transaction.transaction_status)}>
                          <Chip
                            label={TRANSACTION_STATUSES[transaction.transaction_status as TransactionStatus]}
                            size="small"
                            color={getStatusColor(transaction.transaction_status as TransactionStatus) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                            variant="outlined"
                            sx={{
                              fontWeight: 'normal',
                              fontSize: { xs: '0.625rem', sm: '0.75rem' },
                              borderRadius: '12px',
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {formatTransactionDate(String(transaction.transaction_date_time))}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px 4px', sm: '16px' },
                    }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenFilterDrawer(transaction)}
                          title="View Details"
                          sx={{ padding: { xs: '4px', sm: '8px' } }}
                        >
                          <ViewIcon fontSize="small" sx={{ fontSize: { xs: '16px', sm: '20px' } }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={searchTerm ? filteredTransactions.length : totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
        />
      </Paper>

      {/* Transaction Details Drawer */}
      <Drawer
        anchor="right"
        open={isFilterDrawerOpen}
        onClose={handleCloseFilterDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 600 },
            padding: { xs: 1, sm: 2 },
            pt: { xs: 5, sm: 5 },
          },
        }}
      >
        {selectedTransaction && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'right' }}>
              <label onClick={handleCloseFilterDrawer} style={{ cursor: "pointer" }}>
                <CloseIcon style={{ color: "#17A6DA", fontSize: "30px" }} />
              </label>
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ padding: "15px 8px" }}>
                      <Typography>
                        Gateway Transaction ID: <strong>{selectedTransaction.gateway_transaction_id ?? '-'}</strong>
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ padding: "15px 8px" }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#1976d2" }}
                      >
                        Customer Info
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>{selectedTransaction.customer?.customer_name || "N/A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Phone</TableCell>
                    <TableCell>{selectedTransaction.customer?.customer_phone || "N/A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>{selectedTransaction.customer?.customer_email || "N/A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Address</TableCell>
                    <TableCell>{selectedTransaction.customer?.address || "N/A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>City</TableCell>
                    <TableCell>{selectedTransaction.customer?.city || "N/A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Pincode</TableCell>
                    <TableCell>{selectedTransaction.customer?.pincode || "N/A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell>{selectedTransaction.customer?.state || "N/A"}</TableCell>
                  </TableRow>

                  {/* UDF Info */}
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#388e3c", mt: 1 }}
                      >
                        UDF Info
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {Array.from({ length: 10 }, (_, i) => {
                    const key = `udf${i + 1}` as keyof Transaction;
                    const value = selectedTransaction[key] as string | undefined;
                    return (
                      <TableRow key={key}>
                        <TableCell>{`UDF${i + 1}`}</TableCell>
                        <TableCell>
                          {(value ?? "").trim() ? value : "N/A"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Drawer>

      {/* Export Drawer */}
      <ExportDrawer
        open={isExportDrawerOpen}
        onClose={() => setIsExportDrawerOpen(false)}
      />
    </Box>
  );
};

export default TransactionsPage;